import React, { useState } from 'react';
import { User, Clock, CheckCircle2, Star, Award, TrendingUp, Medal } from 'lucide-react';
import { motion } from 'framer-motion';

const StaffPerformanceCard = ({ data }) => {
  const [selectedMetric, setSelectedMetric] = useState('tickets');
  
  const metrics = [
    { id: 'tickets', label: 'Tickets', icon: CheckCircle2 },
    { id: 'response', label: 'Response', icon: Clock }
  ];

  const sortedStaff = data
    .sort((a, b) => selectedMetric === 'tickets'
      ? b.closedTickets - a.closedTickets
      : a.avgResponseTime - b.avgResponseTime)
    .slice(0, 8);

  const getPerformanceLevel = (index) => {
    switch(index) {
      case 0: return { color: 'yellow', label: 'Elite', icon: Star };
      case 1: return { color: 'gray', label: 'Expert', icon: Award };
      case 2: return { color: 'amber', label: 'Senior', icon: Medal };
      default: return { color: 'blue', label: 'Standard', icon: User };
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Staff Performance</h3>
            <p className="text-xs text-gray-500 mt-0.5">Top maintenance personnel</p>
          </div>
          <div className="flex gap-1 bg-gray-100/80 p-1 rounded-lg">
            {metrics.map(metric => (
              <button
                key={metric.id}
                onClick={() => setSelectedMetric(metric.id)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  selectedMetric === metric.id
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <metric.icon className="w-3.5 h-3.5 inline mr-1" />
                {metric.label}
              </button>
            ))}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-gray-50/80">
            <div className="text-xs text-gray-500">Total Resolved</div>
            <div className="mt-1 text-sm font-semibold text-gray-900">
              {data.reduce((acc, staff) => acc + staff.closedTickets, 0)} tickets
            </div>
          </div>
          <div className="p-3 rounded-lg bg-gray-50/80">
            <div className="text-xs text-gray-500">Avg Response</div>
            <div className="mt-1 text-sm font-semibold text-gray-900">
              {(data.reduce((acc, staff) => acc + staff.avgResponseTime, 0) / data.length).toFixed(1)}h
            </div>
          </div>
        </div>
      </div>

      {/* Staff List */}
      <div className="divide-y divide-gray-100">
        {sortedStaff.map((staff, index) => {
          const performance = getPerformanceLevel(index);
          const PerformanceIcon = performance.icon;
          const maxValue = Math.max(...data.map(s => 
            selectedMetric === 'tickets' ? s.closedTickets : s.avgResponseTime
          ));
          const progress = selectedMetric === 'tickets'
            ? (staff.closedTickets / maxValue * 100)
            : ((maxValue - staff.avgResponseTime) / maxValue * 100);
          
          return (
            <motion.div
              key={staff.name}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="group relative p-3 hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                {/* Rank & Icon */}
                <div className={`relative flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${
                  index === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-500' :
                  index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400' :
                  index === 2 ? 'bg-gradient-to-br from-amber-500 to-amber-600' :
                  'bg-blue-100'
                }`}>
                  <PerformanceIcon className={`w-5 h-5 ${
                    index < 3 ? 'text-white' : 'text-blue-600'
                  }`} />
                  {index < 3 && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white shadow-sm flex items-center justify-center text-xs">
                      {index === 0 ? '🏆' : index === 1 ? '🥈' : '🥉'}
                    </div>
                  )}
                </div>

                {/* Staff Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="truncate">
                      <h4 className="font-medium text-gray-900 flex items-center gap-2 text-sm truncate">
                        {staff.name}
                        <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                          index === 0 ? 'bg-yellow-100 text-yellow-700' :
                          index === 1 ? 'bg-gray-100 text-gray-700' :
                          index === 2 ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {performance.label}
                        </span>
                      </h4>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                        <span>{selectedMetric === 'tickets' ? 'Closed Tickets' : 'Response Time'}</span>
                        <span className="text-green-600 flex items-center gap-0.5">
                          <TrendingUp className="w-3.5 h-3.5" />
                          {Math.round(Math.random() * 15)}%
                        </span>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <span className="text-sm font-semibold text-gray-900">
                        {selectedMetric === 'tickets' 
                          ? staff.closedTickets
                          : `${staff.avgResponseTime}h`}
                      </span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-2 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-500 ${
                        index === 0 ? 'bg-gradient-to-r from-yellow-400 to-amber-500' :
                        index === 1 ? 'bg-gradient-to-r from-gray-300 to-gray-400' :
                        index === 2 ? 'bg-gradient-to-r from-amber-500 to-amber-600' :
                        'bg-blue-500'
                      }`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default StaffPerformanceCard; 